import {AsyncPipe, NgIf} from '@angular/common';
import {
  Component,
  EventEmitter,
  Input,
  OnChanges,
  OnDestroy,
  OnInit,
  Output,
  SimpleChanges,
} from '@angular/core';
import {AsyncValidatorFn, Validators} from '@angular/forms';
import {ActivatedRoute} from '@angular/router';
import {CustomValidators, getPropertiesOfType, PropertyFormControl} from '@aztrix/helpers';
import {ErrorType, ProfileContext} from '@aztrix/models';
import {
  InvitationRepresentation,
  ProfileRepresentation,
  PropertyRepresentation,
  PropertyRequest,
  SubPropertyRepresentation,
} from '@aztrix/sdk';
import {ControlsOf, FormControl, FormGroup} from '@ngneat/reactive-forms';
import {UntilDestroy, untilDestroyed} from '@ngneat/until-destroy';
import {Subscription} from 'rxjs';

import {LoginFormComponent} from './components/login-form/login-form.component';
import {OtpLoginFormComponent} from './components/otp-login-form/otp-login-form.component';
import {RegisterFormComponent} from './components/register-form/register-form.component';
import {ResetPasswordFormComponent} from './components/reset-password-form/reset-password-form.component';
import {VerificationFormComponent} from './components/verification-form/verification-form.component';
import {WebidLoginFormComponent} from './components/webid-login-form/webid-login-form.component';
import {ModeService} from './services/mode.service';
@UntilDestroy()
@Component({
  selector: 'ax-auth',
  templateUrl: 'auth.component.html',
  styleUrls: ['auth.component.scss', './components/form.scss'],
  standalone: true,
  imports: [
    NgIf,
    LoginFormComponent,
    OtpLoginFormComponent,
    WebidLoginFormComponent,
    RegisterFormComponent,
    ResetPasswordFormComponent,
    VerificationFormComponent,
    AsyncPipe,
  ],
})
export class AuthComponent implements OnInit, OnChanges, OnDestroy {
  @Input() error: {type: ErrorType; text: string};
  @Input() mode: 'login' | 'register' | 'reset' | 'verification' = 'login';
  @Input() loginMethod: 'password' | 'otp' | 'webid' = 'password';
  @Input() allowMobile = false;
  @Input() alistClaimCode?: string;
  @Input() logo = 'custom:aztrixlogo';
  @Input() individualRegisterAllowed = true;

  @Input() claim: 'username' | 'alistname' = 'username';
  @Input() claimAsyncValidator: AsyncValidatorFn;
  @Output() registerComplete = new EventEmitter();
  @Output() loginComplete = new EventEmitter<{authToken: string | undefined; password?: string}>();

  mode$ = this._modeService.mode$;
  loginMethod$ = this._modeService.loginMethod$;

  resetPassword = new FormGroup({
    username: new FormControl<string>(''),
    password: new FormControl<string>('', [Validators.required, Validators.minLength(8)]),
  });

  loginForm = new FormGroup({
    login: new FormControl<string>(''),
    password: new FormControl<string>(''),
  });

  otpLoginForm = new FormControl('', [CustomValidators.validateLoginValue()]);

  registerForm: FormGroup<ControlsOf<any>> = new FormGroup({
    alistName: new FormControl<string | undefined>(undefined, [
      Validators.required,
      CustomValidators.nameValidator(),
    ]),
    properties: new FormGroup({
      [PropertyRepresentation.TypeEnum.USERNAME]: PropertyFormControl.createByType(
        PropertyRepresentation.TypeEnum.USERNAME,
        [CustomValidators.propertyRequired, CustomValidators.propertyValid]
      ),
      [PropertyRepresentation.TypeEnum.EMAIL]: PropertyFormControl.createByType(
        PropertyRepresentation.TypeEnum.EMAIL,
        [CustomValidators.propertyRequired, CustomValidators.propertyValid]
      ),
      [PropertyRepresentation.TypeEnum.BIRTHDAY]: PropertyFormControl.createByType(
        PropertyRepresentation.TypeEnum.BIRTHDAY,
        [CustomValidators.propertyRequired, CustomValidators.propertyValid]
      ),
      [PropertyRepresentation.TypeEnum.FIRST_NAME]: PropertyFormControl.createByType(
        PropertyRepresentation.TypeEnum.FIRST_NAME,
        [CustomValidators.propertyRequired, CustomValidators.propertyValid]
      ),
      [PropertyRepresentation.TypeEnum.LAST_NAME]: PropertyFormControl.createByType(
        PropertyRepresentation.TypeEnum.LAST_NAME,
        [CustomValidators.propertyRequired, CustomValidators.propertyValid]
      ),
      [PropertyRepresentation.TypeEnum.ORGANISATION_NAME]: PropertyFormControl.createByType(
        PropertyRepresentation.TypeEnum.ORGANISATION_NAME,
        [CustomValidators.propertyRequired, CustomValidators.propertyValid]
      ),
      [PropertyRepresentation.TypeEnum.ORGANISATION_TYPE]: PropertyFormControl.createByType(
        PropertyRepresentation.TypeEnum.ORGANISATION_TYPE,
        [CustomValidators.propertyRequired, CustomValidators.propertyValid]
      ),
      [PropertyRepresentation.TypeEnum.LEGAL_ID]: PropertyFormControl.createByType(
        PropertyRepresentation.TypeEnum.LEGAL_ID,
        [CustomValidators.propertyValid]
      ),
    }),
    legal: new FormGroup({
      terms: new FormControl(false, Validators.requiredTrue),
      privacy: new FormControl(false, Validators.requiredTrue),
      newsletter: new FormControl(false),
    }),
    type: new FormControl<string>(ProfileContext.INDIVIDUAL),
    password: new FormControl<string>('', [Validators.required, Validators.minLength(8)]),
    legalRepresentative: new FormGroup({
      role: new FormControl<string>('', Validators.required),
      properties: new FormGroup({
        [PropertyRepresentation.TypeEnum.FIRST_NAME]: PropertyFormControl.createByType(
          PropertyRepresentation.TypeEnum.FIRST_NAME,
          [CustomValidators.propertyRequired, CustomValidators.propertyValid]
        ),
        [PropertyRepresentation.TypeEnum.LAST_NAME]: PropertyFormControl.createByType(
          PropertyRepresentation.TypeEnum.LAST_NAME,
          [CustomValidators.propertyRequired, CustomValidators.propertyValid]
        ),
        [PropertyRepresentation.TypeEnum.MOBILE_PHONE]: PropertyFormControl.createByType(
          PropertyRepresentation.TypeEnum.MOBILE_PHONE,
          [CustomValidators.propertyRequired, CustomValidators.propertyValid]
        ),
      }),
    }),
  });

  private _subscription = new Subscription();

  constructor(
    private _route: ActivatedRoute,
    private _modeService: ModeService
  ) {}

  ngOnInit(): void {
    if (!this.individualRegisterAllowed) {
      this.registerForm.get('type')?.setValue(ProfileContext.ORGANISATION);
    }
    this._route.queryParams.pipe(untilDestroyed(this)).subscribe((params) => {
      if (params['mode']) {
        this._modeService.mode$.next(params['mode']);
      } else if (this.mode) {
        this._modeService.setMode(params['mode'] ?? this.mode);
      }
    });

    this._subscription = this.mode$.subscribe((mode) => {
      this.mode = mode;
    });

    const inviteData: InvitationRepresentation = this._route.snapshot.data['inviteData'];
    const properties = inviteData?.requestedProperties || [];
    if (inviteData) {
      this.registerForm
        .get('properties')
        ?.get(PropertyRepresentation.TypeEnum.EMAIL)
        ?.get('value')
        ?.setValue(
          this.getInvitePropertyValue(properties, PropertyRepresentation.TypeEnum.USERNAME),
          inviteData.inviteeProfile
        );
      this.registerForm
        .get('properties')
        ?.get(PropertyRepresentation.TypeEnum.LAST_NAME)
        ?.get('value')
        ?.setValue(
          this.getInvitePropertyValue(
            properties,
            PropertyRepresentation.TypeEnum.LAST_NAME,
            inviteData.inviteeProfile
          )
        );
      this.registerForm
        .get('properties')
        ?.get(PropertyRepresentation.TypeEnum.FIRST_NAME)
        ?.get('value')
        ?.setValue(
          this.getInvitePropertyValue(
            properties,
            PropertyRepresentation.TypeEnum.FIRST_NAME,
            inviteData.inviteeProfile
          )
        );
      const birthdayProperties = <SubPropertyRepresentation[]>(
        this.getInvitePropertyValue(
          properties,
          PropertyRepresentation.TypeEnum.BIRTHDAY,
          inviteData.inviteeProfile
        )
      );
      for (const prop of this.registerForm
        .get('properties')
        .get(PropertyRepresentation.TypeEnum.BIRTHDAY)
        .get('properties').controls) {
        prop
          .get('value')
          .setValue(
            birthdayProperties?.find((p) => p.type === prop.get('type').value)?.value || ''
          );
      }
    }
  }

  ngOnChanges(changes: SimpleChanges): void {
    if (changes.mode && this.mode) {
      this._modeService.setMode(this.mode);
    }

    if (changes.loginMethod && this.loginMethod) {
      this._modeService.setLoginMethod(this.loginMethod);
    }

    if (changes.claimAsyncValidator && this.claimAsyncValidator) {
      this.registerForm.get('alistName')?.setAsyncValidators(this.claimAsyncValidator);
      this.registerForm
        .get('properties')
        ?.get(PropertyRepresentation.TypeEnum.USERNAME)
        ?.get('value')
        ?.setAsyncValidators(this.claimAsyncValidator);
    }
  }

  ngOnDestroy(): void {
    this._subscription.unsubscribe();
  }

  get resetPasswordEmail(): string | undefined {
    const loginFormEmail = this.loginForm?.get('login')?.value?.includes('@')
      ? this.loginForm?.get('login')?.value
      : undefined;
    return loginFormEmail || undefined;
  }

  registerCompleted() {
    this.registerComplete.emit();
  }

  getInvitePropertyValue(
    properties: PropertyRequest[],
    type: PropertyRepresentation.TypeEnum,
    profile?: ProfileRepresentation
  ): string | SubPropertyRepresentation[] | undefined {
    if (profile) {
      const profileProperty = getPropertiesOfType(profile, type) as PropertyRepresentation[];
      if (profileProperty && profileProperty.length > 0) {
        return profileProperty[0].value || profileProperty[0].properties;
      }
    }
    const property = properties.find((p) => p.missingProperty?.type === type);
    if (!property) {
      return undefined;
    }
    return property.missingProperty?.value;
  }
}
